import { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { toast } from 'sonner';
import { Search, X, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

function VideoEmbed({ url }) {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (ytMatch) {
        return (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                    className="absolute inset-0 w-full h-full rounded-xl"
                    src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                    title="Mimique guide"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }
    return <video className="w-full max-h-[80vh] rounded-xl object-contain" controls src={url} />;
}

function SectionMedia({ url, type }) {
    if (!url || !type) return null;
    if (type === 'image') {
        return <img src={url} alt="" className="w-full rounded-xl object-cover max-h-80" />;
    }
    return <VideoEmbed url={url} />;
}

/**
 * Shared Help guide content (search, cards, detail view, get-in-touch).
 * Used by both the standalone /help page and the in-app feedback modal so
 * there is a single source of truth. `compact` tightens spacing for the modal.
 */
export default function HelpGuide({ compact = false }) {
    const { user } = useAuth();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [query, setQuery] = useState('');
    const [contactMsg, setContactMsg] = useState('');
    const [videoUrl, setVideoUrl] = useState(null);

    useEffect(() => {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        fetch(`${base}/api/help`, { headers: authHeaders() })
            .then(r => r.json())
            .then(d => setSections(Array.isArray(d.sections) ? d.sections : []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const q = query.trim().toLowerCase();
    const filtered = q
        ? sections.filter(s =>
            (s.title || '').toLowerCase().includes(q) ||
            (s.body || '').toLowerCase().includes(q))
        : sections;

    const handleContact = () => {
        if (!contactMsg.trim()) return;
        const base = process.env.REACT_APP_BACKEND_URL || '';
        try { posthog.identify(user?.id, { email: user?.email, name: user?.name }); } catch { /* noop */ }
        try {
            posthog.capture('user_feedback', {
                message: contactMsg.trim(), page: 'help-guide', source: 'help_no_results',
                search_query: query, email: user?.email, name: user?.name,
            });
        } catch { /* noop */ }
        fetch(`${base}/api/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: contactMsg.trim(), email: user?.email, user_id: user?.id, page: 'help-guide' }),
        }).then(r => r.json()).then(j => { if (!j.ok) console.error('Feedback email failed:', j.error); })
          .catch(err => console.error('Feedback email failed:', err));
        toast.success('Thanks for getting in touch, I’ll get back to you ASAP! 💚');
        setContactMsg('');
    };

    // ── Detail view ─────────────────────────────────────────────────────────
    if (active) {
        const hasMedia = active.media_url && active.media_type;
        return (
            <div className="space-y-4">
                <button
                    onClick={() => setActive(null)}
                    className="text-sm text-stone-400 hover:text-stone-600"
                >
                    ← Back to guides
                </button>
                <h2 className="text-lg font-semibold text-foreground">{active.title}</h2>
                {/* Media floats left on desktop so text wraps around it; stacked (media
                    first) on mobile, and always stacked in the compact dialog, which is
                    narrow at every screen size. */}
                <div className="after:block after:clear-both after:content-['']">
                    {hasMedia && (
                        <div className={compact ? 'mb-4' : 'mb-4 md:float-left md:mb-2 md:mr-6 md:w-1/2'}>
                            <SectionMedia url={active.media_url} type={active.media_type} />
                        </div>
                    )}
                    <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-line">{active.body}</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {!compact && (
                <p className="text-stone-500 text-sm mb-6">Tap any card to learn more and watch video guides</p>
            )}

            {/* Search */}
            {sections.length > 0 && (
                <div className={`relative ${compact ? 'mb-4' : 'mb-8'}`}>
                    <Search className={`absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 ${compact ? 'h-4 w-4 left-3' : 'h-5 w-5'}`} />
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search the guides"
                        className={`w-full rounded-xl bg-white text-foreground placeholder:text-stone-400 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/20 ${compact ? 'pl-9 pr-9 h-10 text-sm' : 'pl-12 pr-11 h-12 text-base'}`}
                    />
                    {query && (
                        <button
                            type="button"
                            onClick={() => setQuery('')}
                            aria-label="Clear search"
                            className={`absolute top-1/2 -translate-y-1/2 text-stone-400 hover:text-primary ${compact ? 'right-2' : 'right-3'}`}
                        >
                            <X className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
                        </button>
                    )}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : sections.length === 0 ? (
                <p className="text-stone-400 text-sm text-center py-10">Help content coming soon.</p>
            ) : filtered.length === 0 ? (
                /* No search results — get in touch */
                <div className="rounded-2xl border border-primary/20 bg-secondary p-5 text-center space-y-3">
                    <p className="text-sm font-semibold text-foreground">No guides matched “{query}”.</p>
                    <p className="text-sm text-stone-600 leading-snug">
                        Can’t find what you’re looking for? Get in touch and I’ll help you out. 💚
                    </p>
                    <textarea
                        className="w-full p-3 border border-stone-200 rounded-lg text-sm resize-none bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                        rows={3}
                        placeholder="Tell me what you’re trying to do…"
                        value={contactMsg}
                        onChange={e => setContactMsg(e.target.value)}
                    />
                    <button
                        onClick={handleContact}
                        disabled={!contactMsg.trim()}
                        className="w-full py-2.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
                    >
                        Get in touch
                    </button>
                </div>
            ) : (
                /* Compact lives in a narrow dialog at every screen size, so it stays
                   one column — a viewport breakpoint would wrongly go two-up there. */
                <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-1 sm:grid-cols-2 gap-4'}`}>
                    {filtered.map(section => (
                        <div
                            key={section.id}
                            className={`rounded-xl border border-stone-200 bg-white hover:border-primary/40 hover:shadow-sm transition-all group ${compact ? 'p-3' : 'p-5'}`}
                        >
                            <button
                                onClick={() => setActive(section)}
                                className="text-left w-full"
                            >
                                <h2 className={`font-semibold text-foreground group-hover:text-primary transition-colors mb-1 ${compact ? 'text-xs' : 'text-sm mb-2'}`}>
                                    {section.title}
                                </h2>
                                <p className={`text-stone-500 leading-relaxed ${compact ? 'text-xs line-clamp-2' : 'text-xs line-clamp-3'}`}>
                                    {section.body}
                                </p>
                            </button>
                            <div className="flex items-center justify-between mt-2">
                                <button
                                    onClick={() => setActive(section)}
                                    className="inline-flex items-center gap-1 text-xs text-stone-400 group-hover:text-primary transition-colors"
                                >
                                    Learn more <span className="text-primary">→</span>
                                </button>
                                {section.media_type === 'video' && section.media_url && (
                                    <button
                                        onClick={() => setVideoUrl(section.media_url)}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    >
                                        <Play className="h-3.5 w-3.5 fill-current" /> Watch video
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Video lightbox — opened straight from a card without leaving the list. */}
            {videoUrl && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setVideoUrl(null)}
                >
                    <div className="relative w-[min(90vw,calc((100vh-6rem)*16/9))]" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setVideoUrl(null)}
                            aria-label="Close video"
                            className="absolute -top-9 right-0 text-white/80 hover:text-white"
                        >
                            <X className="h-6 w-6" />
                        </button>
                        <VideoEmbed url={videoUrl} />
                    </div>
                </div>
            )}
        </div>
    );
}
